/**
 * Очередь записей — по одной на смету.
 *
 * Отложенная запись уходила на сервер, как только срабатывал таймер, — не
 * дожидаясь ответа на предыдущую. Сервер сравнивает отметку `savedAt`, а
 * новая отметка попадает в локальную копию только с ответом: вторая правка,
 * ушедшая раньше ответа на первую, несла старую отметку и получала конфликт
 * с самой собой. На экране это выглядело как «смету изменили в другой
 * вкладке», а правка стиралась. На быстром канале окно в полсекунды, на
 * мобильном интернете — воспроизводится набором цифр подряд.
 *
 * Здесь запись на смету не начинается, пока не ответила предыдущая. Правки,
 * пришедшие за это время, не копятся списком: смета одна, и после ответа
 * уходит её свежее состояние — уже с новой отметкой.
 *
 * Чистая функция без React: `save` получает id и сам берёт свежую смету в
 * момент отправки, а не в момент постановки в очередь.
 */
export interface SaveQueue {
  /** запись сейчас или после текущей; возвращает обещание текущего цикла */
  push(id: string): Promise<void>;
  /** есть ли запись в полёте или в ожидании */
  busy(id?: string): boolean;
  /**
   * Дождаться, пока по смете не останется записей — ни в полёте, ни в ожидании.
   *
   * Нужно переходу согласования: он читает смету из базы, и если последняя
   * правка ещё летит, руководителю уйдёт версия без неё. Сбой записи здесь
   * не пробрасывается — о нём скажет сама запись.
   */
  idle(id: string): Promise<void>;
}

export function createSaveQueue(save: (id: string) => Promise<void>): SaveQueue {
  const running = new Map<string, Promise<void>>();
  const pending = new Set<string>();

  async function run(id: string): Promise<void> {
    try {
      await save(id);
    } finally {
      running.delete(id);
      // Пока писали, смету правили снова — пишем ещё раз, уже свежую.
      if (pending.delete(id)) running.set(id, run(id));
    }
  }

  return {
    push(id) {
      const current = running.get(id);
      if (current) {
        pending.add(id);
        return current;
      }
      const job = run(id);
      running.set(id, job);
      return job;
    },
    busy(id) {
      if (id) return running.has(id) || pending.has(id);
      return running.size > 0 || pending.size > 0;
    },
    async idle(id) {
      // Цикл, а не одно ожидание: пока писали, могла встать следующая запись,
      // и `run` уже подменил обещание новым.
      while (running.has(id)) await running.get(id)!.catch(() => undefined);
    },
  };
}
